import { Minimize2, Play, Plus } from "lucide-react";
import { CSSProperties, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router";

import { useDocumentTitle } from "../../../../use-document-title";
import { useSecondaryHeader } from "../../../../secondary-header-context";
import { ScrollShadow, TABLE_HEADER_HEIGHT } from "../../../../scroll-shadow";
import { InfiniteScrollSentinel, SkeletonRow } from "../../../../skeleton-row";
import { useInfiniteRows } from "../../../../use-infinite-rows";
import { useScrollEdges } from "../../../../use-scroll-edges";
import { useSimulatedLoad } from "../../../../use-simulated-load";
import styles from "./definitions.module.css";

type WorkflowDefinition = {
  id: string;
  name: string;
  description: string;
  /** `false` for internal/chained workflows with no manual "Run" action
   * (e.g. "Send Notification") — mirrors the reference dashboard. */
  runnable: boolean;
};

/**
 * Real workflow names/descriptions from the reference dashboard (see the
 * compact "Workflows" card in `../summary-cards.tsx`), extended with a few
 * more plausible definitions in the same style — some cross-referencing the
 * `workflowName`s that already show up as individual runs in
 * `../runs-table.tsx` (e.g. `syncCalendarEvents`, `refreshPortfolioMetrics`).
 * Static mock content — there is no live workflow-orchestration backend in
 * `platform` yet.
 */
const WORKFLOW_DEFINITIONS: WorkflowDefinition[] = [
  {
    id: "weekly-sentiment-summary",
    name: "Weekly Sentiment Summary",
    description:
      "Monday digest of the previous week's sentiment forms with an AI-generated overview, emailed to the sentiment whitelist.",
    runnable: true,
  },
  {
    id: "sentiment-form-chaser",
    name: "Sentiment Form Chaser",
    description:
      "Per-member reminder of any sentiment forms assigned in the last week that are still outstanding.",
    runnable: true,
  },
  {
    id: "agenda-reminder",
    name: "Agenda Reminder",
    description:
      "Friday reminder to the sentiment recipients to submit agenda items for the next investment meeting.",
    runnable: true,
  },
  {
    id: "daily-report-alerts",
    name: "Daily Report Alerts",
    description:
      "Refresh every report with an active daily subscription and notify subscribers of new rows.",
    runnable: true,
  },
  {
    id: "weekly-report-alerts",
    name: "Weekly Report Alerts",
    description:
      "Refresh every report with an active weekly subscription and notify subscribers of new rows.",
    runnable: true,
  },
  {
    id: "send-notification",
    name: "Send Notification",
    description:
      "Dispatch a single notification job (all enabled channels) — internal, enqueued per job.",
    runnable: false,
  },
  {
    id: "identify-company-update",
    name: "Identify Company Update",
    description:
      "Classify one ingested email and, when positive, chain a metrics extraction run.",
    runnable: true,
  },
  {
    id: "extract-metrics",
    name: "Extract Metrics",
    description: "Extract company metrics from one email and persist the draft metrics.",
    runnable: true,
  },
  {
    id: "eval-metrics-extraction",
    name: "Eval Metrics Extraction",
    description: "Score the metrics extractor against the expected-results set and persist the run.",
    runnable: true,
  },
  {
    id: "ingest-gmail-inbox",
    name: "Ingest Gmail — Inbox",
    description: "Finite, self-rescheduling sync of one Workspace inbox onto S3 and the message index.",
    runnable: true,
  },
  {
    id: "ingest-gmail-manager",
    name: "Ingest Gmail — Manager",
    description: "Hourly reconciliation of the active Workspace inbox set. Singleton run.",
    runnable: true,
  },
  {
    id: "sync-calendar-events",
    name: "Sync Calendar Events",
    description:
      "Sync partner Google Calendar events into the meetings index for agenda and availability lookups.",
    runnable: true,
  },
  {
    id: "refresh-portfolio-metrics",
    name: "Refresh Portfolio Metrics",
    description:
      "Recompute portfolio-wide valuation and ownership metrics from the latest cap table and reporting data.",
    runnable: true,
  },
  {
    id: "enrich-company-profile",
    name: "Enrich Company Profile",
    description:
      "Pull the latest public signals (funding, headcount, news) for one portfolio company and merge into its profile.",
    runnable: true,
  },
  {
    id: "score-deal-sentiment",
    name: "Score Deal Sentiment",
    description: "Run the sentiment model over one deal's diligence notes and persist the resulting score.",
    runnable: true,
  },
  {
    id: "generate-weekly-digest",
    name: "Generate Weekly Digest",
    description: "Compile the cross-portfolio weekly digest email from the last 7 days of ingested updates.",
    runnable: true,
  },
  {
    id: "archive-stale-threads",
    name: "Archive Stale Threads",
    description:
      "Move inbox threads with no activity in 90+ days into cold storage — internal, run by the inbox manager.",
    runnable: false,
  },
];

/**
 * This catalog holds a few more curated internal workflow definitions than
 * the hand-authored sample above — a real deployment accumulates plenty of
 * narrow, one-off chained workflows over time, but the set stays bounded
 * (nothing like the unbounded run-history log). `TOTAL_DEFINITION_COUNT` is
 * the accurate total this page's header count claims, and
 * `getDefinition(index)` extends `WORKFLOW_DEFINITIONS` out to that count
 * with deterministic synthetic rows for infinite scroll, rather than
 * pretending the catalog stops where the hand-written sample happens to
 * stop.
 */
const TOTAL_DEFINITION_COUNT = 34;

/** Deterministic 32-bit string hash (FNV-1a) + seeded PRNG — same technique
 * as `../pages/runs.tsx`'s `generateSyntheticRun`, so the SAME index always
 * produces the SAME synthetic row (no `Math.random()`/`Date.now()`). */
function hashSeed(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Action verbs lifted straight out of the hand-authored names above (Sync
 * Calendar Events, Refresh Portfolio Metrics, Enrich Company Profile, Score
 * Deal Sentiment, Generate Weekly Digest, Archive Stale Threads, Extract
 * Metrics, Eval Metrics Extraction, Ingest Gmail — Inbox, Identify Company
 * Update, Send Notification) — synthetic names recombine these rather than
 * inventing a new unrelated vocabulary. */
const VERB_POOL = [
  "Sync",
  "Refresh",
  "Enrich",
  "Score",
  "Generate",
  "Archive",
  "Extract",
  "Eval",
  "Ingest",
  "Identify",
  "Send",
] as const;

/** Object phrases, same provenance as `VERB_POOL` — pulled from the tail end
 * of the hand-authored names above. */
const OBJECT_POOL = [
  "Calendar Events",
  "Portfolio Metrics",
  "Company Profile",
  "Deal Sentiment",
  "Weekly Digest",
  "Stale Threads",
  "Gmail Inbox",
  "Metrics Extraction",
  "Company Update",
  "Sentiment Summary",
  "Report Alerts",
  "Notification",
  "Agenda Reminder",
] as const;

/** Description sentence shapes for runnable synthetic definitions, styled
 * after the hand-authored descriptions above (present-tense verb phrase,
 * ends with a period, references persisting/scheduling the result). */
const DESCRIPTION_TEMPLATES: Array<(verb: string, object: string) => string> = [
  (verb, object) =>
    `${verb} the latest ${object.toLowerCase()} and persist the results to the workflow index.`,
  (verb, object) =>
    `Scheduled job to ${verb.toLowerCase()} ${object.toLowerCase()} across the active portfolio set.`,
  (verb, object) =>
    `Nightly sweep to ${verb.toLowerCase()} ${object.toLowerCase()} and flag anomalies for review.`,
];

/** Description shapes for the `runnable: false` case — mirrors the two
 * internal/chained descriptions above ("Send Notification", "Archive Stale
 * Threads"), which both call out that there's no manual "Run" action. */
const INTERNAL_DESCRIPTION_TEMPLATES: Array<(verb: string, object: string) => string> = [
  (verb, object) =>
    `${verb} ${object.toLowerCase()} for one record at a time — internal, chained from an upstream run.`,
  (verb, object) =>
    `Dispatches a single ${object.toLowerCase()} job — internal, enqueued per run, no manual trigger.`,
];

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const EXISTING_DEFINITION_NAMES = new Set(
  WORKFLOW_DEFINITIONS.map((definition) => definition.name),
);

/** A definition beyond the hand-authored `WORKFLOW_DEFINITIONS` catalog —
 * deterministic per `index`, so scrolling to the same position always shows
 * the same row. */
function generateSyntheticDefinition(index: number): WorkflowDefinition {
  const rand = mulberry32(hashSeed(`definition-${index}`));

  const verb = VERB_POOL[Math.floor(rand() * VERB_POOL.length)];
  let objectIndex = Math.floor(rand() * OBJECT_POOL.length);
  let object = OBJECT_POOL[objectIndex];
  let name = `${verb} ${object}`;

  // Nudge to the next object in the pool, deterministically, rather than
  // reproducing a name that exactly matches one of the hand-authored
  // definitions above (e.g. rolling "Sync" + "Calendar Events").
  if (EXISTING_DEFINITION_NAMES.has(name)) {
    objectIndex = (objectIndex + 1) % OBJECT_POOL.length;
    object = OBJECT_POOL[objectIndex];
    name = `${verb} ${object}`;
  }

  // Ratio matches the hand-authored set above (15 runnable / 2 internal-only
  // out of 17 ≈ 88% runnable).
  const runnable = rand() > 0.12;

  const description = runnable
    ? DESCRIPTION_TEMPLATES[Math.floor(rand() * DESCRIPTION_TEMPLATES.length)](
        verb,
        object,
      )
    : INTERNAL_DESCRIPTION_TEMPLATES[
        Math.floor(rand() * INTERNAL_DESCRIPTION_TEMPLATES.length)
      ](verb, object);

  return {
    // Index suffix guarantees a unique id even when two different indices
    // happen to roll the same verb/object pair (11 verbs × 13 objects is
    // only 143 combos) — matters here because `id` doubles as this table's
    // React row `key`.
    id: `${slugify(name)}-${index}`,
    name,
    description,
    runnable,
  };
}

function getDefinition(index: number): WorkflowDefinition {
  return index < WORKFLOW_DEFINITIONS.length
    ? WORKFLOW_DEFINITIONS[index]
    : generateSyntheticDefinition(index);
}

/**
 * `/workflows/definitions` "maximised" full-page table: every workflow
 * definition (Name, Description, Actions) as a full-width table, rather than
 * the dashboard card's compact list-row style (see `../summary-cards.tsx`).
 * Breadcrumbs already read "Workflows > Workflow definitions" for this
 * route, so this renders only the table card — no page-level title of its
 * own. All data is static mock content — there is no live
 * workflow-orchestration backend in `platform` yet.
 */
export function WorkflowDefinitionsPage() {
  useDocumentTitle("Workflow Definitions");

  // Initial page-mount loading state — distinct from `isLoadingMore` below,
  // which covers the infinite-scroll "load more" append. This app has no
  // real backend, so nothing actually fetches, but the table still shows a
  // believable skeleton for the first ~500ms rather than snapping straight
  // to the final rows.
  const isInitialLoading = useSimulatedLoad();

  const navigate = useNavigate();
  const goToDashboard = () => {
    if (!document.startViewTransition) {
      navigate("/workflows");
      return;
    }
    document.startViewTransition(() => flushSync(() => navigate("/workflows")));
  };

  const { ref: tableWrapRef, atTop, atBottom } = useScrollEdges<HTMLDivElement>();
  const { loadedCount, isLoadingMore, hasMore, sentinelRef } = useInfiniteRows({
    totalCount: TOTAL_DEFINITION_COUNT,
    initialCount: WORKFLOW_DEFINITIONS.length,
    pageSize: 20,
    scrollRootRef: tableWrapRef,
  });
  const [runnableOnly, setRunnableOnly] = useState(false);

  // Filtering applies to whatever's been loaded so far, same as any
  // client-side filter over an infinite-scrolled list — it doesn't fetch
  // ahead to find matches beyond `loadedCount`.
  const loadedDefinitions = useMemo(
    () => Array.from({ length: loadedCount }, (_, index) => getDefinition(index)),
    [loadedCount],
  );

  const visibleDefinitions = useMemo(
    () =>
      runnableOnly
        ? loadedDefinitions.filter((definition) => definition.runnable)
        : loadedDefinitions,
    [loadedDefinitions, runnableOnly],
  );

  const isFiltered = runnableOnly;

  const filterRow = useMemo(
    () => (
      <div className={styles.filterRow}>
        <div className={styles.filterTabs} role="tablist" aria-label="Filter workflow definitions">
          <button
            type="button"
            role="tab"
            aria-selected={!runnableOnly}
            className={`${styles.filterTab} ${!runnableOnly ? styles.filterTabActive : ""}`}
            onClick={() => setRunnableOnly(false)}
          >
            All definitions
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={runnableOnly}
            className={`${styles.filterTab} ${runnableOnly ? styles.filterTabActive : ""}`}
            onClick={() => setRunnableOnly(true)}
          >
            Runnable only
          </button>
        </div>
        {/* Structural spacer, matching QA Automation's `.tabBarRow` toolbar
         * pattern (filters packed left, flex spacer pushing any action
         * button hard right) — the reference dashboard's "Workflows" section
         * has no header-level action button today, so there's nothing to
         * render on the right yet, but the row is laid out ready for one. */}
        <span className={styles.spacer} />
        <button type="button" className={styles.startRunButton}>
          <Plus size={14} aria-hidden />
          New definition
        </button>
      </div>
    ),
    [runnableOnly],
  );

  useSecondaryHeader(filterRow);

  return (
    <div className={styles.page}>
      <section
        className={styles.card}
        style={{ viewTransitionName: "wf-card-definitions" } as CSSProperties}
      >
        <header className={styles.header}>
          <h2 className={styles.title}>Workflow definitions</h2>
          <span className={styles.count}>
            {isFiltered
              ? `${visibleDefinitions.length} of ${loadedCount} loaded`
              : TOTAL_DEFINITION_COUNT}
          </span>
          <button
            type="button"
            className={styles.minimiseButton}
            onClick={goToDashboard}
            aria-label="Minimise to dashboard"
            title="Minimise to dashboard"
          >
            <Minimize2 className={styles.minimiseIcon} aria-hidden="true" />
          </button>
        </header>

        <div className={styles.scrollShadowWrapper}>
        <div ref={tableWrapRef} className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th} scope="col">
                  Name
                </th>
                <th className={styles.th} scope="col">
                  Description
                </th>
                <th className={`${styles.th} ${styles.thActions}`} scope="col">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {isInitialLoading ? (
                Array.from({ length: 8 }, (_, index) => (
                  <SkeletonRow key={`initial-skeleton-${index}`} columns={3} />
                ))
              ) : (
                <>
                  {visibleDefinitions.map((definition) => (
                    <tr key={definition.id} className={styles.row}>
                      <td className={styles.td}>
                        <div className={styles.name}>{definition.name}</div>
                        <div className={styles.id}>{definition.id}</div>
                      </td>
                      <td className={`${styles.td} ${styles.description}`}>
                        {definition.description}
                      </td>
                      <td className={`${styles.td} ${styles.actionsCell}`}>
                        {definition.runnable ? (
                          <button type="button" className={styles.runButton}>
                            <Play className={styles.runIcon} aria-hidden="true" />
                            Run
                          </button>
                        ) : (
                          <span className={styles.noAction} title="Internal/chained workflow — no manual run">
                            —
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {hasMore && <InfiniteScrollSentinel sentinelRef={sentinelRef} columns={3} />}
                  {isLoadingMore &&
                    Array.from({ length: 3 }, (_, index) => (
                      <SkeletonRow key={`skeleton-${index}`} columns={3} />
                    ))}
                </>
              )}
            </tbody>
          </table>
        </div>
        <ScrollShadow atTop={atTop} atBottom={atBottom} topOffset={TABLE_HEADER_HEIGHT} />
        </div>
      </section>
    </div>
  );
}
